<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useRightPanelResize } from 'dashboard/composables/useRightPanelResize';
import { useMapGetter } from 'dashboard/composables/store';
import { useSelectedVaultSageBot } from 'dashboard/composables/useSelectedVaultSageBot';
import { useVaultSageConversation } from 'dashboard/composables/useVaultSageConversation';
import { useAlert } from 'dashboard/composables';
import wootConstants from 'dashboard/constants/globals';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import VaultSageAPI from 'dashboard/api/vaultsage';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();
const { panelStyle, onResizeStart, resetPanelWidth } = useRightPanelResize();
const { width: windowWidth } = useWindowSize();

const currentChat = useMapGetter('getSelectedChat');
const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const { isVaultSageConnected, hasActiveVaultSageBot, activeVaultSageBot } =
  useVaultSageConversation();
const {
  selectedVaultSageBot,
  selectedVaultSageBotName,
  clearSelectedVaultSageBot,
} = useSelectedVaultSageBot();

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const panelVaultSageBot = computed(
  () => selectedVaultSageBot.value || activeVaultSageBot.value
);
const panelVaultSageBotName = computed(
  () => selectedVaultSageBotName.value || panelVaultSageBot.value?.name || ''
);

const shouldShowSagePanel = computed(() => {
  const isSageEnabled = isFeatureEnabledonAccount.value(
    currentAccountId.value,
    FEATURE_FLAGS.SAGE
  );
  const { is_sage_panel_open: isSagePanelOpen } = uiSettings.value;
  return (
    isSageEnabled &&
    !!isSagePanelOpen &&
    !!currentChat.value?.id &&
    isVaultSageConnected.value &&
    (!!panelVaultSageBot.value || hasActiveVaultSageBot.value)
  );
});

const closeSagePanel = () => {
  if (isSmallScreen.value && uiSettings.value?.is_sage_panel_open) {
    clearSelectedVaultSageBot();
    updateUISettings({
      is_contact_sidebar_open: false,
      is_copilot_panel_open: false,
      is_sage_panel_open: false,
    });
  }
};

const closeSagePanelExplicit = () => {
  clearSelectedVaultSageBot();
  updateUISettings({
    is_sage_panel_open: false,
  });
};

const messages = ref([]);
const draft = ref('');
const isSending = ref(false);

watch(
  () => currentChat.value?.id,
  () => {
    messages.value = [];
    draft.value = '';
    clearSelectedVaultSageBot();
  }
);

watch(
  () => uiSettings.value?.is_sage_panel_open,
  isOpen => {
    if (!isOpen) {
      clearSelectedVaultSageBot();
    }
  }
);

const sendMessage = async () => {
  const text = draft.value.trim();
  const conversationId = currentChat.value?.id;
  if (!text || !conversationId || isSending.value) return;

  messages.value = [...messages.value, { role: 'user', content: text }];
  draft.value = '';
  isSending.value = true;

  try {
    const { data } = await VaultSageAPI.ask(
      conversationId,
      text,
      panelVaultSageBot.value?.id
    );
    const content = data.content || '';
    if (!content) {
      useAlert(data.message || t('CONVERSATION.SAGE.EMPTY_REPLY'));
    } else {
      messages.value = [...messages.value, { role: 'assistant', content }];
    }
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.message ||
        t('CONVERSATION.SAGE.EMPTY_REPLY')
    );
  } finally {
    isSending.value = false;
  }
};
</script>

<template>
  <div
    v-if="shouldShowSagePanel"
    v-on-click-outside="() => closeSagePanel()"
    class="relative bg-n-surface-2 h-full shrink-0 flex flex-col fixed top-0 ltr:right-0 rtl:left-0 z-40 w-full md:w-auto max-w-sm md:max-w-none transition-transform duration-300 ease-in-out md:relative ltr:border-l rtl:border-r border-n-weak shadow-lg md:shadow-none"
    :class="[
      {
        'md:flex': shouldShowSagePanel,
        'md:hidden': !shouldShowSagePanel,
      },
    ]"
    :style="isSmallScreen ? undefined : panelStyle"
  >
    <div
      v-if="!isSmallScreen"
      class="absolute top-0 bottom-0 z-50 w-4 cursor-col-resize ltr:left-0 rtl:right-0"
      @pointerdown.stop.prevent="onResizeStart"
      @mousedown.stop.prevent="onResizeStart"
      @touchstart.stop.prevent="onResizeStart"
      @dblclick.stop="resetPanelWidth"
    >
      <div
        class="absolute top-1/2 flex h-16 w-3 -translate-y-1/2 items-center justify-center rounded-full border border-n-weak bg-n-surface-1 shadow-sm transition-colors hover:border-n-strong hover:bg-n-alpha-1 ltr:left-0.5 rtl:right-0.5"
      >
        <div class="flex flex-col gap-1">
          <span class="h-1 w-1 rounded-full bg-n-slate-10" />
          <span class="h-1 w-1 rounded-full bg-n-slate-10" />
          <span class="h-1 w-1 rounded-full bg-n-slate-10" />
        </div>
      </div>
    </div>
    <div class="flex h-full flex-col overflow-hidden">
      <div
        class="flex items-start justify-between gap-2 px-4 py-3 border-b border-n-weak"
      >
        <div class="min-w-0">
          <p class="text-sm font-semibold text-n-slate-12">
            {{ $t('CONVERSATION.SAGE.PANEL_TITLE') }}
          </p>
          <p class="text-xs text-n-slate-11 mt-0.5">
            {{
              $t('CONVERSATION.SAGE.PANEL_SUBTITLE', {
                name: panelVaultSageBotName,
              })
            }}
          </p>
        </div>
        <Button
          slate
          faded
          sm
          icon="i-lucide-x"
          class="shrink-0"
          @click="closeSagePanelExplicit"
        />
      </div>

      <div class="flex-1 min-h-0 overflow-y-auto px-4 py-3 flex flex-col gap-3">
        <p
          v-if="!messages.length && !isSending"
          class="text-sm text-n-slate-11"
        >
          {{ $t('CONVERSATION.SAGE.PANEL_EMPTY') }}
        </p>
        <div
          v-for="(msg, idx) in messages"
          :key="idx"
          class="rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words"
          :class="
            msg.role === 'user'
              ? 'bg-n-alpha-2 text-n-slate-12 self-end max-w-[95%]'
              : 'bg-n-iris-3 text-n-slate-12 self-start max-w-[95%]'
          "
        >
          {{ msg.content }}
        </div>
        <div
          v-if="isSending"
          class="flex items-center gap-2 text-sm text-n-iris-11"
        >
          <Icon icon="i-lucide-loader-2" class="size-4 animate-spin" />
          <span>{{ $t('CONVERSATION.SAGE.PANEL_THINKING') }}</span>
        </div>
      </div>

      <div
        class="border-t border-n-weak p-3 flex flex-col gap-2 bg-n-surface-2 shrink-0"
      >
        <textarea
          v-model="draft"
          rows="2"
          class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-brand resize-none"
          :placeholder="$t('CONVERSATION.SAGE.PANEL_INPUT_PLACEHOLDER')"
          :disabled="isSending"
          @keydown.enter.exact.prevent="sendMessage"
        />
        <div class="flex justify-end">
          <Button
            :label="$t('CONVERSATION.SAGE.PANEL_SEND')"
            size="sm"
            :is-loading="isSending"
            :disabled="!draft.trim()"
            @click="sendMessage"
          />
        </div>
      </div>
    </div>
  </div>
  <template v-else />
</template>
